import { Link, useLocation } from "react-router";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import ArrowDownIcon from "../icons/arrow-down";

type SidebarItemProps = {
  item: any;
  isSidebarOpen: boolean;
};

const SidebarItem = ({ item, isSidebarOpen }: SidebarItemProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isChildrenOpen, setIsChildrenOpen] = useState(false);

  const onHandleChildren = useCallback(
    () => setIsChildrenOpen((prev) => !prev),
    [],
  );

  const isParentActive = pathname === item.path;
  const isChildActive = item.children?.some((child) => pathname === child.path);
  const isActive = isParentActive || isChildActive;

  useEffect(() => {
    if (isChildActive) {
      setIsChildrenOpen(true);
    }
  }, [isChildActive]);

  const items: MenuProps["items"] = item?.children?.map((child, i) => ({
    label: <Link to={child.path || "#"}>{t(child.label)}</Link>,
    key: i,
  })) as MenuProps["items"];

  if (item?.path && !item.children) {
    return (
      <li>
        <Link
          to={item.path || "#"}
          className={twMerge(
            "flex items-center gap-3 rounded-[12px] group p-2 font-medium hover:bg-[#6E46FF]",
            isSidebarOpen ? "" : "justify-center",
            isActive ? "bg-[#6E46FF]" : "",
          )}
        >
          {item?.icon && (
            <item.icon
              className={twMerge(
                "text-2xl transition group-hover:text-white",
                isActive ? "text-white" : "text-[#6E46FF]",
              )}
            />
          )}

          {isSidebarOpen && (
            <span
              className={twMerge(
                "line-clamp-1 flex flex-1 items-center group-hover:text-white",
                isActive ? "text-[#ffffff]" : "text-[#0E0E0E]",
              )}
            >
              {t(item.label)}
            </span>
          )}
        </Link>
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-2">
      {isSidebarOpen ? (
        <button
          onClick={onHandleChildren}
          className={twMerge(
            "flex w-full items-center gap-2 rounded p-2 font-medium hover:bg-[#2563EB4D]",
            "justify-between",
            isParentActive ? "bg-[#2563EB4D]" : "",
            isChildActive ? "" : "",
          )}
        >
          {item?.icon && (
            <item.icon
              className={twMerge(
                "text-2xl transition",
                isActive ? "text-primary" : "text-[#69757A]",
              )}
            />
          )}

          {isSidebarOpen && (
            <span
              className={twMerge(
                "line-clamp-1 flex flex-1 items-center",
                isActive ? "text-[#0E0E0E]" : "text-[#0E0E0E]",
              )}
            >
              {t(item.label)}
            </span>
          )}

          {isSidebarOpen && (
            <div className={isChildrenOpen ? "rotate-180" : ""}>
              <ArrowDownIcon className="text-white" />
            </div>
          )}
        </button>
      ) : (
        <Dropdown menu={{ items }} trigger={["click"]}>
          <button
            onClick={onHandleChildren}
            className={twMerge(
              "flex w-full items-center gap-2 rounded p-2 font-medium hover:bg-[#F8F8FA]",
              "justify-center",
              isActive ? "" : "",
            )}
          >
            {item?.icon && (
              <item.icon
                className={twMerge(
                  "text-2xl transition",
                  isActive ? "text-primary" : "text-[#69757A]",
                )}
              />
            )}
          </button>
        </Dropdown>
      )}
    </li>
  );
};

export default SidebarItem;
